/** Minimal tween manager. Render-side only; never touches sim state. */
export type Ease = (t: number) => number;

export const Easing = {
  linear: (t: number) => t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inQuad: (t: number) => t * t,
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  outElastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin(((t * 10 - 0.75) * (2 * Math.PI)) / 3) + 1;
  },
  outBounce: (t: number) => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
    if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
} satisfies Record<string, Ease>;

export interface Tween {
  /** Elapsed ms. */
  t: number;
  duration: number;
  ease: Ease;
  /** Called with eased progress 0..1 each frame. */
  update: (p: number) => void;
  onComplete?: () => void;
  done: boolean;
  /** Optional tag so a group can be cancelled together (e.g. per sprite). */
  tag?: object;
}

export class Tweens {
  private list: Tween[] = [];

  add(duration: number, update: (p: number) => void, opts: { ease?: Ease; onComplete?: () => void; tag?: object; delay?: number } = {}): Tween {
    const tw: Tween = { t: -(opts.delay ?? 0), duration: Math.max(1, duration), ease: opts.ease ?? Easing.outQuad, update, onComplete: opts.onComplete, done: false, tag: opts.tag };
    this.list.push(tw);
    return tw;
  }

  /** Cancel every tween carrying this tag (no completion callback). */
  cancel(tag: object): void {
    for (const tw of this.list) if (tw.tag === tag) tw.done = true;
  }

  update(dt: number): void {
    for (const tw of this.list) {
      if (tw.done) continue;
      tw.t += dt;
      if (tw.t < 0) continue;
      const p = Math.min(1, tw.t / tw.duration);
      tw.update(tw.ease(p));
      if (p >= 1) {
        tw.done = true;
        tw.onComplete?.();
      }
    }
    if (this.list.length > 64 || this.list.some((t) => t.done)) this.list = this.list.filter((t) => !t.done);
  }

  get active(): number {
    return this.list.filter((t) => !t.done).length;
  }
}
